import { useEffect, useMemo, useRef, useState } from 'react';
import { modules, type ViewId } from './demo-data';
import {
  decryptBackup,
  downloadJson,
  encryptBackup,
  parseAppState,
  readJsonFile,
  type AppState,
  type EncryptedBackup,
} from './persistence';

export function CommandPalette({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: ViewId) => void;
}) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const results = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return modules.filter((module) => !normalized || `${module.label} ${module.description}`.toLowerCase().includes(normalized));
  }, [query]);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    window.setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  if (!open) return null;
  return <div className="modal-backdrop" role="presentation" onMouseDown={onClose}>
    <section className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette" onMouseDown={(event) => event.stopPropagation()}>
      <div className="command-input"><span>⌕</span><input ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search modules and controls…" /><kbd>ESC</kbd></div>
      <div className="command-results">
        {results.map((module) => <button type="button" key={module.id} onClick={() => { onNavigate(module.id); onClose(); }}>
          <span>{module.shortLabel.slice(0, 2).toUpperCase()}</span><div><strong>{module.label}</strong><small>{module.description}</small></div><b>Open</b>
        </button>)}
        {results.length === 0 && <p>No matching module.</p>}
      </div>
    </section>
  </div>;
}

export function SetupBanner({
  state,
  onChange,
  onOpenSettings,
}: {
  state: AppState;
  onChange: (next: AppState) => void;
  onOpenSettings: () => void;
}) {
  if (state.onboardingComplete) return null;
  return <section className="setup-banner">
    <div><span>INITIAL SETUP</span><strong>Make this operating system yours.</strong><p>Confirm the human owner, active profile and mandate limits before connecting real services.</p></div>
    <div><button type="button" onClick={onOpenSettings}>Review setup</button><button type="button" className="quiet-button" onClick={() => onChange({ ...state, onboardingComplete: true })}>Use demo defaults</button></div>
  </section>;
}

export function SettingsPanel({
  open,
  state,
  fallback,
  onClose,
  onChange,
}: {
  open: boolean;
  state: AppState;
  fallback: AppState;
  onClose: () => void;
  onChange: (next: AppState) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [message, setMessage] = useState('Local state is stored only in this browser.');

  if (!open) return null;

  const exportEncrypted = async () => {
    const passphrase = window.prompt('Create a backup passphrase (minimum 8 characters).');
    if (!passphrase) return;
    try {
      downloadJson('personal-agency-os.encrypted.json', await encryptBackup(state, passphrase));
      setMessage('Encrypted backup created with AES-GCM.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Encrypted export failed.');
    }
  };

  const importBackup = async (file: File) => {
    try {
      const raw = await readJsonFile(file);
      let payload = raw;
      if (raw && typeof raw === 'object' && (raw as Partial<EncryptedBackup>).format === 'personal-agency-os.encrypted-backup') {
        const passphrase = window.prompt('Enter the backup passphrase.');
        if (!passphrase) return;
        payload = await decryptBackup(raw as EncryptedBackup, passphrase);
      }
      onChange(parseAppState(payload, fallback));
      setMessage('Backup imported and validated.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Import failed.');
    }
  };

  return <div className="drawer-backdrop" role="presentation" onMouseDown={onClose}>
    <aside className="settings-drawer" role="dialog" aria-modal="true" aria-label="Settings" onMouseDown={(event) => event.stopPropagation()}>
      <header><div><span>PRODUCT CONTROL</span><h2>Settings & portability</h2></div><button type="button" onClick={onClose} aria-label="Close settings">×</button></header>
      <label>Human owner<input value={state.ownerName} onChange={(event) => onChange({ ...state, ownerName: event.target.value })} /></label>
      <label>Default profile<select value={state.profile} onChange={(event) => onChange({ ...state, profile: event.target.value === 'personal' ? 'personal' : 'professional' })}><option value="personal">Personal</option><option value="professional">Professional</option></select></label>
      <label className="check-row"><input type="checkbox" checked={state.onboardingComplete} onChange={(event) => onChange({ ...state, onboardingComplete: event.target.checked })} /><span>Initial setup completed</span></label>

      <section><span className="drawer-label">PORTABILITY</span><p>Your data model is exportable. No product action requires vendor lock-in.</p><div className="drawer-actions"><button type="button" onClick={() => downloadJson('personal-agency-os.backup.json', state)}>Export JSON</button><button type="button" onClick={exportEncrypted}>Encrypted backup</button><button type="button" onClick={() => fileRef.current?.click()}>Import backup</button></div><input ref={fileRef} hidden type="file" accept="application/json,.json" onChange={(event) => { const file = event.target.files?.[0]; if (file) void importBackup(file); event.currentTarget.value = ''; }} /></section>
      <section><span className="drawer-label">LOCAL DATA</span><p>{message}</p><button type="button" className="danger-button" onClick={() => { if (window.confirm('Reset all locally stored state and receipts?')) { onChange(fallback); setMessage('Local state reset.'); } }}>Reset local state</button></section>
      <footer><span>State version {state.version}</span><span>Updated {new Date(state.updatedAt).toLocaleString()}</span></footer>
    </aside>
  </div>;
}
